import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { QRCodeSVG } from 'qrcode.react';
import HealthPassCard from '../HealthPassCard';
import { publicSelfScreenAPI } from '../../../services/api';
import { PublicLang, tPublic } from '../publicScreenI18n';

interface Props {
  screening: Record<string, unknown>;
  /** Public /screen — WhatsApp parity: high-risk = QR only; cleared = green pass + QR + PDF */
  publicSafe?: boolean;
  lang?: PublicLang;
}

function downloadQrPng(filename: string) {
  const svg = document.getElementById('public-pass-qr');
  if (!svg) throw new Error('QR not found');
  const xml = new XMLSerializer().serializeToString(svg);
  const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  return new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const size = 512;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        canvas.toBlob((png) => {
          URL.revokeObjectURL(url);
          if (!png) {
            reject(new Error('png'));
            return;
          }
          const a = document.createElement('a');
          a.href = URL.createObjectURL(png);
          a.download = filename;
          a.click();
          URL.revokeObjectURL(a.href);
          resolve();
        }, 'image/png');
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('img'));
    };
    img.src = url;
  });
}

const PassDisplayStep: React.FC<Props> = ({ screening, publicSafe = false, lang = 'en' }) => {
  const passRecord = screening.pass_record as Record<string, unknown> | undefined;
  const [downloading, setDownloading] = useState(false);

  if (!passRecord) {
    return <Box sx={{ p: 2, color: 'text.secondary' }}>{tPublic(lang, 'no_pass')}</Box>;
  }

  const isCleared = passRecord.pass_type === 'cleared';
  const token = typeof passRecord.pass_token === 'string' ? passRecord.pass_token : '';
  const qrValue = token
    ? `${window.location.origin}/pass/${token}`
    : `${window.location.origin}/screen`;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (isCleared) {
        if (!token) throw new Error('token');
        await publicSelfScreenAPI.downloadPassPdf(token);
      } else {
        await downloadQrPng('ebola_health_pass_qr.png');
      }
    } catch {
      window.alert(tPublic(lang, 'err_download'));
    } finally {
      setDownloading(false);
    }
  };

  const downloadBtn = publicSafe ? (
    <Box sx={{ mt: 2, width: '100%' }}>
      <Button
        fullWidth
        variant="outlined"
        size="large"
        onClick={handleDownload}
        disabled={downloading || (isCleared && !token) || (!isCleared && !token)}
        startIcon={downloading ? <CircularProgress size={18} /> : <DownloadIcon />}
        sx={{
          minHeight: 48,
          borderRadius: '12px',
          fontWeight: 700,
          textTransform: 'none',
          borderColor: isCleared ? '#1a7a3c' : '#8b0000',
          color: isCleared ? '#1a7a3c' : '#8b0000',
          '&:hover': {
            borderColor: isCleared ? '#146c43' : '#6b0000',
            bgcolor: isCleared ? 'rgba(26,122,60,0.06)' : 'rgba(139,0,0,0.06)',
          },
        }}
      >
        {downloading
          ? tPublic(lang, 'downloading')
          : isCleared
            ? tPublic(lang, 'download_pass')
            : tPublic(lang, 'download_qr')}
      </Button>
    </Box>
  ) : null;

  // Public high-risk: WhatsApp parity — QR only (no red pass card)
  if (publicSafe && !isCleared) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        <Box
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            p: 2.5,
            borderRadius: '14px',
            border: '2px solid #8b0000',
            bgcolor: 'rgba(139,0,0,0.04)',
          }}
        >
          <QRCodeSVG
            id="public-pass-qr"
            value={qrValue}
            size={220}
            fgColor="#8b0000"
            bgColor="#ffffff"
            level="H"
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#8b0000', maxWidth: 260, lineHeight: 1.4 }}>
            {tPublic(lang, 'qr_caption')}
          </Typography>
        </Box>
        {downloadBtn}
      </Box>
    );
  }

  return (
    <Box>
      <HealthPassCard
        publicSafe={publicSafe}
        passRecord={passRecord as unknown as Parameters<typeof HealthPassCard>[0]['passRecord']}
      />
      {downloadBtn}
    </Box>
  );
};

export default PassDisplayStep;
